import React from 'react';
import {TextInputProps} from 'react-native';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import Btn from 'components/Btn';
import CardEntryFields, {CardEntryFieldsTheme} from 'components/CardEntryFields';
import Div from 'lib/Div';
import P from 'lib/P';
import type {CardTokenizationStatus} from 'hooks/useCardTokenization';
import {styles} from '../styles';

type Props = {
  accountHolderName: string, cardNumberInputProps: TextInputProps, expiryInputProps: TextInputProps,
  cvcInputProps: TextInputProps, saveCard: boolean, formError: string, isSubmittingDeposit: boolean,
  tokenizationStatus: CardTokenizationStatus, tokenizationError: string, onSaveCardToggle: () => void,
  onContinue: () => void, onCancel: () => void, onRetryTokenization: () => void
};

const cardEntryFieldsTheme: CardEntryFieldsTheme = {
  labelProps: {
    fontFamily: 'POI Aeronaut', fs: 11, lh: 16.5, ls: 1.76, tt: 'uppercase', color: '#97a8a6', mb: 6,
    style: styles.mediumText
  },
  inputStyle: styles.input, inputWithIconTextStyle: styles.inputWithIconText, inputIconStyle: styles.inputIcon,
  placeholderTextColor: '#6e817f', iconColor: '#6e817f'
};

const CardDetailsStep = (props: Props) => {
  const {
    accountHolderName, cardNumberInputProps, expiryInputProps, cvcInputProps, saveCard, formError, isSubmittingDeposit,
    tokenizationStatus, tokenizationError
  } = props;
  const isPreparingTokenization = tokenizationStatus === 'loading';
  const isTokenizationReady = tokenizationStatus === 'ready';

  return (
    <Div gap={24}>
      <Div testID="add-card-info-form-container" bg="#143030" rounded={12} p={20} gap={14}>
        <CardEntryFields
          accountHolderName={accountHolderName} cardNumberInputProps={cardNumberInputProps}
          expiryInputProps={expiryInputProps} cvcInputProps={cvcInputProps} disabled={isSubmittingDeposit}
          theme={cardEntryFieldsTheme}
        />

        <Btn row alignItems="center" gap={10} mt={4} onPress={props.onSaveCardToggle} disabled={isSubmittingDeposit}
             accessibilityRole="checkbox" accessibilityState={{checked: saveCard}}>
          <Div w={18} h={18} rounded={3} borderWidth={1} borderColor={saveCard ? '#27e9a6' : '#97a8a6'}
               bg={saveCard ? '#27e9a6' : 'transparent'} alignItems="center" justifyContent="center">
            {saveCard && <MaterialIcons name="check" size={15} color="#0e2424"/>}
          </Div>

          <P fontFamily="POI Aeronaut" fs={14} lh={21} color="#97a8a6">
            Save this card for future deposits
          </P>
        </Btn>

        {isPreparingTokenization && (
          <P fontFamily="POI Aeronaut" fs={13} lh={18.2} color="#97a8a6">
            Preparing secure card entry...
          </P>
        )}

        {tokenizationStatus === 'error' && (
          <Div gap={10}>
            <P fontFamily="POI Aeronaut" fs={13} lh={18.2} color="#ff6969">{tokenizationError}</P>
            <Btn onPress={props.onRetryTokenization} accessibilityRole="button">
              <P fontFamily="POI Aeronaut" fs={13} lh={18.2} color="#27e9a6">
                Retry secure connection
              </P>
            </Btn>
          </Div>
        )}

        {!!formError && formError !== tokenizationError && (
          <P fontFamily="POI Aeronaut" fs={13} lh={18.2} color="#ff6969">{formError}</P>
        )}
      </Div>

      <Div gap={10}>
        <Btn h={52} w="100%" px={28} rounded={999} bg="#27e9a6" alignItems="center" justifyContent="center"
             onPress={props.onContinue} disabled={isSubmittingDeposit || !isTokenizationReady}
             opacity={isSubmittingDeposit || !isTokenizationReady ? 0.65 : 1} accessibilityRole="button">
          <P fontFamily="POI Aeronaut" fs={14} lh={21} ls={0.84} tt="uppercase" color="#1b3131"
             style={styles.mediumText}>
            {isPreparingTokenization ? 'Preparing...' : 'Add Card & Continue'}
          </P>
        </Btn>

        <Btn h={52} w="100%" px={28} rounded={999} bg="transparent" alignItems="center" justifyContent="center"
             onPress={props.onCancel} disabled={isSubmittingDeposit} accessibilityRole="button">
          <P fontFamily="POI Aeronaut" fs={14} lh={21} ls={0.84} tt="uppercase" color="#27e9a6"
             style={styles.mediumText}>
            Cancel
          </P>
        </Btn>
      </Div>
    </Div>
  );
}

export default CardDetailsStep;
